import { useId } from "react";

// Three diagrams for "where computation lives today". All share one frame:
// a 300×190 canvas, a gateway box at y=14 (140 tall), caption at y=184.

const GW = { x: 20, y: 14, w: 260, h: 158 };
const TRACES = {
  cyan: "M0 0c6-8 10 6 16-2s10-10 16 0 10 6 16-4 10 8 16 0",
  amber: "M0 0h8l4-10 5 18 4-8h12l4 6 5-12 4 6h18",
  steel: "M0 0l4-6 4 8 4-10 4 12 4-8 4 6 4-8 4 10 4-10 4 8 4-6 4 8 4-10 4 8 4-4 4 4",
};
const COLORS = { cyan: "var(--mk-cyan)", amber: "var(--mk-amber)", steel: "var(--mk-steel)" };
type Kind = keyof typeof TRACES;

function Gateway({ w = GW.w }: { w?: number }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const gradId = `gw-grad-${id}`;
  return (
    <g transform={`translate(${GW.x} ${GW.y})`}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="rgba(255,255,255,.09)" />
          <stop offset="1" stopColor="rgba(255,255,255,.01)" />
        </linearGradient>
      </defs>
      <rect width={w} height={GW.h} rx="2" className="gateway" style={{ fill: `url(#${gradId})`, strokeWidth: 0.5 }} />
      <text x="16" y="22" className="svg-k" style={{ fontSize: 10.5, fill: "var(--mk-fg)", fontWeight: 600 }}>Ignition gateway</text>
    </g>
  );
}

function TagRow({ x, y, kind, name, w = 64 }: { x: number; y: number; kind: Kind; name: string; w?: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect width={w} height="30" rx="2" className="node" />
      <rect width="3" height="30" rx="1.5" fill={COLORS[kind]} />
      <text x="10" y="11" className="svg-s" style={{ fontSize: 8.5, fill: "var(--mk-fg)" }}>{name}</text>
      <g transform={`translate(10 21)`}>
        <path d={TRACES[kind]} fill="none" stroke={COLORS[kind]} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" transform="scale(0.72 0.6)" />
      </g>
    </g>
  );
}

function Caption({ children }: { children: string }) {
  return (
    <text x="150" y="202" textAnchor="middle" className="svg-s" style={{ fontSize: 11 }}>{children}</text>
  );
}

export function ScriptsGraphic() {
  // Each tag has its own script and its own output. Nothing is shared.
  const rows: Array<[Kind, string, string]> = [["cyan", "pressure", "alarm I"], ["amber", "current", "alarm II"], ["steel", "vibration", "alarm III"]];
  return (
    <svg className="mk-svg" viewBox="0 0 300 208" role="img" aria-label="Inside the gateway, each tag feeds its own isolated script and output. Nothing is shared between them.">
      <Gateway />
      {rows.map(([kind, name, out], i) => {
        const y = 50 + i * 42;
        return (
          <g key={name}>
            <TagRow x={36} y={y} kind={kind} name={name} />
            <path d={`M100 ${y + 15}h22`} className="pipe" />
            <g transform={`translate(122 ${y + 5})`}>
              <rect width="46" height="20" rx="2" className="node" style={{ stroke: "var(--mk-red)", strokeWidth: 0.5 }} />
              <text x="23" y="14" textAnchor="middle" fill="var(--mk-fg)" style={{ fontSize: 9, fontFamily: "var(--font-mono)" }}>script</text>
            </g>
            <path d={`M168 ${y + 15}h22`} className="pipe" />
            <g transform={`translate(190 ${y + 5})`}>
              <rect width="74" height="20" rx="2" className="node" />
              <text x="37" y="14" textAnchor="middle" className="svg-s" style={{ fontSize: 8.5, fill: "var(--mk-fg)" }}>{out}</text>
            </g>
            {/* each script fires on its own schedule: sample in, alarm out */}
            <circle r="3" className="particle" style={{ offsetPath: `path('M100 ${y + 15}h22')`, ["--dur" as string]: `${2.2 + i * 0.7}s`, ["--delay" as string]: `${i * 0.9}s` }} />
            <circle r="3" className="particle-red" style={{ offsetPath: `path('M168 ${y + 15}h22')`, ["--dur" as string]: `${2.2 + i * 0.7}s`, ["--delay" as string]: `${i * 0.9 + 0.5}s` }} />
            <rect x="122" y={y + 5} width="46" height="20" rx="2" fill="none" stroke="var(--mk-red)" strokeWidth="0.5" className="flash" style={{ ["--dur" as string]: `${2.2 + i * 0.7}s`, ["--delay" as string]: `${i * 0.9 + 0.3}s` }} />
          </g>
        );
      })}
      <Caption>no shared state · not time-aware</Caption>
    </svg>
  );
}

function Cloud({ x, y }: { x: number; y: number }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <path d="M22 44a14 14 0 0 1 2-28 20 20 0 0 1 38-6 16 16 0 0 1 20 16 12 12 0 0 1-4 18z" fill="rgba(255,255,255,.04)" stroke="rgba(255,255,255,.28)" strokeWidth="1.5" strokeLinejoin="round" />
      <text x="42" y="34" textAnchor="middle" className="svg-k" style={{ fontSize: 10.5, fill: "var(--mk-fg)", fontWeight: 600 }}>Cloud</text>
    </g>
  );
}

export function ExportGraphic() {
  // History leaves the gateway to the cloud; the answer comes back hours later.
  const up = "M140 98 C 160 74, 180 64, 216 58";
  const back = "M258 64 C 280 90, 266 126, 197 142";
  return (
    <svg className="mk-svg" viewBox="0 0 300 208" role="img" aria-label="Tags are archived to history inside the gateway, exported to the cloud for analysis, and the result slowly returns to the gateway.">
      <Gateway w={176} />
      <TagRow x={30} y={50} kind="cyan" name="pressure" />
      <TagRow x={30} y={92} kind="amber" name="current" />
      <TagRow x={30} y={134} kind="steel" name="vibration" />
      <path d="M94 65 C 102 65, 100 92, 108 92 M94 107h12 M94 149 C 102 149, 100 122, 108 122" className="pipe" />
      {/* history cylinder */}
      <g transform="translate(106 87)">
        <rect width="30" height="40" rx="2" className="node" />
        <ellipse cx="15" cy="6" rx="2" ry="4" fill="var(--mk-night-3)" stroke="rgba(255,255,255,.2)" />
        <path d="M2 18a13 4 0 0 0 26 0M2 30a13 4 0 0 0 26 0" fill="none" stroke="rgba(255,255,255,.14)" />
      </g>
      <text x="121" y="139" textAnchor="middle" className="svg-s" style={{ fontSize: 8.5 }}>history</text>
      {/* export: crosses the gateway wall */}
      <path d={up} className="pipe" />
      <path d={up} className="pipe-lit" />
      <circle r="3" className="particle" style={{ offsetPath: `path('${up}')`, ["--dur" as string]: "2.4s" }} />
      <text x="168" y="58" textAnchor="middle" className="svg-s" style={{ fontSize: 8.5 }}>export</text>
      <Cloud x={200} y={22} />
      <text x="234" y="88" textAnchor="middle" className="svg-s" style={{ fontSize: 8.5 }}>analysis</text>
      {/* slow, dashed return into the gateway */}
      <path d={back} className="pipe" strokeDasharray="3 5" />
      <path d="M204 137l-7 5 7 5" fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="1.5" />
      {/* the answer crawls back, much slower than the export */}
      <circle r="3" className="particle-red" style={{ offsetPath: `path('${back}')`, ["--dur" as string]: "7s", ["--delay" as string]: "1.2s" }} />
      <Caption>offline · not in the live path</Caption>
    </svg>
  );
}

export function CoprocessorGraphic() {
  // Tags → module (sync · window · SQL) → derived tag, looped back into the tree.
  const inPaths = [50, 92, 134].map((y) => `M100 ${y + 15} C 112 ${y + 15}, 112 107, 124 107`);
  const outPath = "M206 107h18";
  return (
    <svg className="mk-svg" viewBox="0 0 300 208" role="img" aria-label="Inside the gateway, three tags flow into the Coprocessor module, which synchronizes and windows them in SQL and emits one derived tag.">
      <Gateway />
      <TagRow x={36} y={50} kind="cyan" name="pressure" />
      <TagRow x={36} y={92} kind="amber" name="current" />
      <TagRow x={36} y={134} kind="steel" name="vibration" />
      {inPaths.map((d, i) => (
        <g key={i}>
          <path d={d} className="pipe" />
          <path d={d} className="pipe-lit" />
          <circle r="3" className="particle" style={{ offsetPath: `path('${d}')`, ["--dur" as string]: "2.2s", ["--delay" as string]: `${i * 0.5}s` }} />
        </g>
      ))}
      {/* module */}
      <g transform="translate(124 77)">
        <rect width="82" height="60" rx="2" fill="var(--mk-night)" stroke="var(--mk-cyan)" strokeWidth="1.5" />
        <text x="41" y="18" textAnchor="middle" fill="var(--mk-cyan)" style={{ fontSize: 9.5, fontFamily: "var(--font-mono)", letterSpacing: ".08em" }}>COPROCESSOR</text>
        <text x="41" y="34" textAnchor="middle" className="svg-s" style={{ fontSize: 8.5 }}>sync · window</text>
        <rect x="24" y="40" width="34" height="14" rx="2" fill="rgba(79,216,236,.12)" stroke="rgba(79,216,236,.5)" />
        <text x="41" y="50" textAnchor="middle" fill="var(--mk-cyan)" style={{ fontSize: 8.5, fontFamily: "var(--font-mono)" }}>SQL</text>
      </g>
      {/* derived tag */}
      <path d={outPath} className="pipe" />
      <path d={outPath} className="pipe-out" />
      <circle r="3" className="particle-out" style={{ offsetPath: `path('${outPath}')`, ["--dur" as string]: "1.4s" }} />
      <g transform="translate(224 88)">
        <rect width="46" height="38" rx="2" className="node" style={{ stroke: "var(--mk-green)" }} />
        <text x="23" y="15" textAnchor="middle" className="svg-s" style={{ fontSize: 8.5, fill: "var(--mk-fg)" }}>health</text>
        <text x="23" y="30" textAnchor="middle" fill="var(--mk-green)" style={{ fontSize: 11, fontFamily: "var(--font-mono)", fontWeight: 600 }}>100</text>
      </g>
      <Caption>stateful · windowed · live</Caption>
    </svg>
  );
}
